/**
 * My Affiliate Earnings Dashboard (Creator View)
 * Shows creator's earnings, statistics, and performance metrics
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  AttachMoney as AttachMoneyIcon,
  Visibility as VisibilityIcon,
  ShoppingCart as ShoppingCartIcon,
} from '@mui/icons-material';
import { supabase } from '../../supabaseClient';
import { useTranslation } from 'react-i18next';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line } from 'recharts';
import { useDarkMode, getClozedStyles } from '../../hooks/useDarkMode';
import { useAuth } from '../../components/AuthProvider';

interface Earning {
  id: string;
  commission_amount: number;
  status: 'pending' | 'confirmed' | 'paid' | 'cancelled';
  earned_at: string;
  confirmed_at: string | null;
  paid_at: string | null;
  product: {
    product_name: string;
  } | null;
  content: {
    short_code: string;
  } | null;
  brand: {
    store_name: string;
  } | null;
}

interface EarningsSummary {
  total_pending: number;
  total_confirmed: number;
  total_paid: number;
  total_cancelled: number;
  total_earnings: number;
  count_pending: number;
  count_confirmed: number;
  count_paid: number;
}

interface ContentPerformance {
  content_id: string;
  short_code: string;
  clicks: number;
  orders: number;
  earnings: number;
  conversion_rate: number;
}

export default function MyAffiliateEarnings() {
  const { t } = useTranslation();
  const isDarkMode = useDarkMode();
  const styles = getClozedStyles(isDarkMode);
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [creatorId, setCreatorId] = useState<string | null>(null);
  const [earnings, setEarnings] = useState<Earning[]>([]);
  const [summary, setSummary] = useState<EarningsSummary | null>(null);
  const [contentPerformance, setContentPerformance] = useState<ContentPerformance[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [timeRange, setTimeRange] = useState<string>('30days');

  useEffect(() => {
    loadCreatorProfile();
  }, [user]);

  useEffect(() => {
    if (creatorId) {
      Promise.all([loadEarnings(), loadSummary(), loadContentPerformance()]);
    }
  }, [creatorId, timeRange]);

  const loadCreatorProfile = async () => {
    try {
      if (!user) {
        setError('로그인이 필요합니다.');
        setLoading(false);
        return;
      }

      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('id')
        .eq('id', user.id)
        .single();

      if (profileError) throw profileError;

      const { data: sellerProfile, error: sellerError } = await supabase
        .from('seller_profile')
        .select('id')
        .eq('user_id', profile.id)
        .single();

      if (sellerError) throw sellerError;

      setCreatorId(sellerProfile.id);
    } catch (err: any) {
      setError('크리에이터 프로필을 불러올 수 없습니다: ' + err.message);
    }
  };

  const getDateFilter = () => {
    const now = new Date();
    const ranges: Record<string, string> = {
      '7days': new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString(),
      '30days': new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString(),
      '90days': new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000).toISOString(),
      'all': '2000-01-01T00:00:00.000Z',
    };
    return ranges[timeRange];
  };

  const loadEarnings = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('affiliate_earnings')
        .select(
          `
          *,
          products!inner(product_name),
          contents!inner(short_code),
          seller_profile!affiliate_earnings_brand_id_fkey(store_name)
        `
        )
        .eq('creator_id', creatorId)
        .gte('earned_at', getDateFilter())
        .order('earned_at', { ascending: false });

      if (error) throw error;

      // Transform the data to match our interface
      const transformedData = (data || []).map((item: any) => ({
        ...item,
        product: item.products,
        content: item.contents,
        brand: item.seller_profile,
      }));

      setEarnings(transformedData);
    } catch (err: any) {
      setError('수익 목록을 불러올 수 없습니다: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadSummary = async () => {
    try {
      const { data, error } = await supabase
        .from('affiliate_earnings')
        .select('commission_amount, status')
        .eq('creator_id', creatorId)
        .gte('earned_at', getDateFilter());

      if (error) throw error;

      const summary: EarningsSummary = {
        total_pending: 0,
        total_confirmed: 0,
        total_paid: 0,
        total_cancelled: 0,
        total_earnings: 0,
        count_pending: 0,
        count_confirmed: 0,
        count_paid: 0,
      };

      (data || []).forEach((earning) => {
        const amount = earning.commission_amount;
        if (earning.status === 'pending') {
          summary.total_pending += amount;
          summary.count_pending++;
        } else if (earning.status === 'confirmed') {
          summary.total_confirmed += amount;
          summary.count_confirmed++;
        } else if (earning.status === 'paid') {
          summary.total_paid += amount;
          summary.count_paid++;
        } else if (earning.status === 'cancelled') {
          summary.total_cancelled += amount;
        }
      });

      summary.total_earnings = summary.total_pending + summary.total_confirmed + summary.total_paid;

      setSummary(summary);
    } catch (err: any) {
      setError('요약 정보를 불러올 수 없습니다: ' + err.message);
    }
  };

  const loadContentPerformance = async () => {
    try {
      // Get clicks per content
      const { data: clicksData, error: clicksError } = await supabase
        .from('affiliate_clicks')
        .select('content_id')
        .eq('creator_id', creatorId)
        .gte('clicked_at', getDateFilter());

      if (clicksError) throw clicksError;

      // Get orders and earnings per content
      const { data: earningsData, error: earningsError } = await supabase
        .from('affiliate_earnings')
        .select('content_id, commission_amount, content:contents(short_code)')
        .eq('creator_id', creatorId)
        .in('status', ['confirmed', 'paid'])
        .gte('earned_at', getDateFilter());

      if (earningsError) throw earningsError;

      // Aggregate data
      const contentMap: Record<
        string,
        { content_id: string; short_code: string; clicks: number; orders: number; earnings: number }
      > = {};

      // Count clicks
      (clicksData || []).forEach((click) => {
        if (!contentMap[click.content_id]) {
          contentMap[click.content_id] = {
            content_id: click.content_id,
            short_code: '',
            clicks: 0,
            orders: 0,
            earnings: 0,
          };
        }
        contentMap[click.content_id].clicks++;
      });

      // Count orders and earnings
      (earningsData || []).forEach((earning: any) => {
        if (!contentMap[earning.content_id]) {
          contentMap[earning.content_id] = {
            content_id: earning.content_id,
            short_code: earning.content?.short_code || '',
            clicks: 0,
            orders: 0,
            earnings: 0,
          };
        }
        contentMap[earning.content_id].short_code = earning.content?.short_code || '';
        contentMap[earning.content_id].orders++;
        contentMap[earning.content_id].earnings += earning.commission_amount;
      });

      // Calculate conversion rates and convert to array
      const performance: ContentPerformance[] = Object.values(contentMap).map((item) => ({
        ...item,
        conversion_rate: item.clicks > 0 ? (item.orders / item.clicks) * 100 : 0,
      }));

      // Sort by earnings
      performance.sort((a, b) => b.earnings - a.earnings);

      setContentPerformance(performance.slice(0, 10)); // Top 10
    } catch (err: any) {
      setError('콘텐츠 성과를 불러올 수 없습니다: ' + err.message);
    }
  };

  const getStatusChip = (status: string) => {
    const statusConfig = {
      pending: { label: '대기중', color: 'warning' as const },
      confirmed: { label: '확정', color: 'success' as const },
      paid: { label: '지급완료', color: 'info' as const },
      cancelled: { label: '취소됨', color: 'error' as const },
    };

    const config = statusConfig[status as keyof typeof statusConfig];
    return <Chip label={config.label} color={config.color} size="small" />;
  };

  return (
    <Box sx={styles.pageContainer}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" fontWeight="bold" sx={styles.title}>
          어필리에이트 수익
        </Typography>
        <FormControl sx={{ minWidth: 150 }}>
          <InputLabel sx={styles.text}>기간</InputLabel>
          <Select value={timeRange} onChange={(e) => setTimeRange(e.target.value)} label="기간" sx={styles.input} MenuProps={{ PaperProps: { sx: styles.selectMenu } }}>
            <MenuItem value="7days" sx={styles.menuItem}>최근 7일</MenuItem>
            <MenuItem value="30days" sx={styles.menuItem}>최근 30일</MenuItem>
            <MenuItem value="90days" sx={styles.menuItem}>최근 90일</MenuItem>
            <MenuItem value="all" sx={styles.menuItem}>전체</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Summary Cards */}
      {summary && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={3}>
            <Card sx={styles.paper}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <AttachMoneyIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="body2" color="text.secondary" sx={styles.text}>
                    총 수익
                  </Typography>
                </Box>
                <Typography variant="h4" fontWeight="bold" sx={styles.text}>
                  {summary.total_earnings.toLocaleString()}원
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card sx={styles.paper}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <TrendingUpIcon color="success" sx={{ mr: 1 }} />
                  <Typography variant="body2" color="text.secondary" sx={styles.text}>
                    확정 수익
                  </Typography>
                </Box>
                <Typography variant="h5" fontWeight="bold" sx={styles.text}>
                  {summary.total_confirmed.toLocaleString()}원
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={styles.text}>
                  {summary.count_confirmed}건
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card sx={styles.paper}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <ShoppingCartIcon color="warning" sx={{ mr: 1 }} />
                  <Typography variant="body2" color="text.secondary" sx={styles.text}>
                    대기 중
                  </Typography>
                </Box>
                <Typography variant="h5" fontWeight="bold" sx={styles.text}>
                  {summary.total_pending.toLocaleString()}원
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={styles.text}>
                  {summary.count_pending}건
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card sx={styles.paper}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <VisibilityIcon color="info" sx={{ mr: 1 }} />
                  <Typography variant="body2" color="text.secondary" sx={styles.text}>
                    지급 완료
                  </Typography>
                </Box>
                <Typography variant="h5" fontWeight="bold" sx={styles.text}>
                  {summary.total_paid.toLocaleString()}원
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={styles.text}>
                  {summary.count_paid}건
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Content Performance */}
      {contentPerformance.length > 0 && (
        <Card sx={{ ...styles.paper, mb: 3 }}>
          <CardContent>
            <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
              콘텐츠별 성과 (Top 10)
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>콘텐츠</TableCell>
                    <TableCell align="right">클릭수</TableCell>
                    <TableCell align="right">주문수</TableCell>
                    <TableCell align="right">전환율</TableCell>
                    <TableCell align="right">수익</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {contentPerformance.map((content) => (
                    <TableRow key={content.content_id}>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {content.short_code || content.content_id.substring(0, 8)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">{content.clicks.toLocaleString()}</TableCell>
                      <TableCell align="right">{content.orders.toLocaleString()}</TableCell>
                      <TableCell align="right">
                        <Chip label={`${content.conversion_rate.toFixed(1)}%`} size="small" color="primary" />
                      </TableCell>
                      <TableCell align="right">
                        <Typography fontWeight="bold">{content.earnings.toLocaleString()}원</Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Earnings List */}
      <Card sx={styles.paper}>
        <CardContent>
          <Typography variant="h6" fontWeight="bold" sx={{ ...styles.text, mb: 2 }}>
            수익 내역
          </Typography>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer sx={styles.tableContainer}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>날짜</TableCell>
                    <TableCell>브랜드</TableCell>
                    <TableCell>제품</TableCell>
                    <TableCell>콘텐츠</TableCell>
                    <TableCell>상태</TableCell>
                    <TableCell align="right">커미션</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {earnings.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary" sx={styles.text}>수익 내역이 없습니다</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    earnings.map((earning) => (
                      <TableRow key={earning.id}>
                        <TableCell>{new Date(earning.earned_at).toLocaleDateString()}</TableCell>
                        <TableCell>{earning.brand?.store_name || '정보 없음'}</TableCell>
                        <TableCell>{earning.product?.product_name || '정보 없음'}</TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={styles.text}>{earning.content?.short_code || '정보 없음'}</Typography>
                        </TableCell>
                        <TableCell>{getStatusChip(earning.status)}</TableCell>
                        <TableCell align="right">
                          <Typography fontWeight="bold" sx={styles.text}>{earning.commission_amount.toLocaleString()}원</Typography>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
